import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AnyObject, ObjectSchema } from 'yup';

import { FormStepId } from 'shared/config/formStepId';

import type { TCreditFormCommonFields, TCreditFormState } from 'entities/creditForm';
import {
  setIsStepFullFilledByStepId,
  WorkplaceFormValidationSchema,
  CreditPurposeFormValidationSchema,
  ContactsFormValidationSchema,
  PassportFormValidationSchema,
  AdditionalInfoFormValidationSchema,
  selectAllFormFields,
  setAutoValidationInProgress,
} from 'entities/creditForm';
import type { TUserState } from 'entities/user';
import { accountSelector } from 'entities/user';

export type TStepsValidateErrors = Array<{
  formStepId: ValuesOf<typeof FormStepId>;
  errorFields: Array<keyof TCreditFormCommonFields>;
}>;

const validateStep = async (
  validationSchema: ObjectSchema<AnyObject>,
  formFields: Partial<TCreditFormCommonFields>,
  formStepId: ValuesOf<typeof FormStepId>,
  errorSteps: TStepsValidateErrors,
) => {
  try {
    await validationSchema.validate(formFields, { abortEarly: true });
  } catch (err) {
    if (err.path) {
      errorSteps.push({
        formStepId,
        errorFields: [err.path],
      });
    }
    return false;
  }
  return true;
};

export type TValidateUserDataThunkPayload = {
  onComplete?: () => void | Promise<void>;
  onSuccess?: (
    formFields: TCreditFormCommonFields,
    lastValidatedStepId: ValuesOf<typeof FormStepId>,
  ) => void | Promise<void>;
  onStepSuccess?: (stepId: ValuesOf<typeof FormStepId>) => void | Promise<void>;
  onError?: (
    stepsValidateErrors: TStepsValidateErrors,
    validatedSteps: Array<ValuesOf<typeof FormStepId>>,
  ) => void | Promise<void>;
  stepsToValidate?: Array<ValuesOf<typeof FormStepId>>;
};

export const validateUserDataThunk = createAsyncThunk<
  void,
  TValidateUserDataThunkPayload,
  { state: TCreditFormState & TUserState }
>(
  'validateUserData/validateUserDataThunk',
  // eslint-disable-next-line max-statements
  async ({ onComplete, onSuccess, onError, stepsToValidate, onStepSuccess }, { dispatch, getState }) => {
    const user = accountSelector(getState());
    dispatch(setAutoValidationInProgress(true));
    const steps = !user
      ? [FormStepId.CreditPurposeInfo]
      : stepsToValidate || [
          FormStepId.CreditPurposeInfo,
          FormStepId.ContactsInfo,
          FormStepId.PassportInfo,
          FormStepId.WorkplaceInfo,
          FormStepId.AdditionalInfo,
        ];

    const formFields = selectAllFormFields(getState());
    const errorSteps: TStepsValidateErrors = [];

    const schemaByStep: Record<ValuesOf<typeof FormStepId>, () => ObjectSchema<AnyObject>> = {
      [FormStepId.CreditPurposeInfo]: () => CreditPurposeFormValidationSchema(),
      [FormStepId.ContactsInfo]: () => ContactsFormValidationSchema(true),
      [FormStepId.PassportInfo]: () => PassportFormValidationSchema(),
      [FormStepId.WorkplaceInfo]: () => WorkplaceFormValidationSchema(),
      [FormStepId.AdditionalInfo]: () => AdditionalInfoFormValidationSchema(),
    };

    const successfullyValidatedSteps: Array<ValuesOf<typeof FormStepId>> = [];

    for (const stepId of steps) {
      // Валидация должна идти последовательно, чтобы остановиться на первом шаге с ошибкой
      // eslint-disable-next-line no-await-in-loop
      const isStepValid = await validateStep(schemaByStep[stepId](), formFields, stepId, errorSteps);

      dispatch(
        setIsStepFullFilledByStepId({
          stepId,
          isFullFilled: isStepValid,
        }),
      );

      if (!isStepValid) {
        break;
      }

      successfullyValidatedSteps.push(stepId);
      // eslint-disable-next-line no-await-in-loop
      await onStepSuccess?.(stepId);
    }

    const isSuccess = successfullyValidatedSteps.length === steps.length;

    if (isSuccess) {
      const lastValidatedStepId = successfullyValidatedSteps[successfullyValidatedSteps.length - 1];
      await onSuccess?.(formFields, lastValidatedStepId);
    } else {
      await onError?.(errorSteps, successfullyValidatedSteps);
    }
    await onComplete?.();
    dispatch(setAutoValidationInProgress(false));
  },
);
